import React, { useState, useEffect, useRef } from "react";
import { Navbar, Container, Nav } from "react-bootstrap";
import { Link } from "react-router-dom";
import logo from "../../assets/images/my_logo4.png";
import NewsBanner from "../home/banner";

const WA_LINK = "https://chat.whatsapp.com/LNfGI3UFWbt69VeUbBoxLj";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Sports", to: "/sports" },
  { label: "Auto", to: "/auto" },
  { label: "Technology", to: "/technology" },
  { label: "Entertainment", to: "/entertainment" },
  { label: "Health", to: "/health" },
  { label: "Immigration", to: "/immigration" },
  { label: "Heritage", to: "/heritage" },
  { label: "Travel", to: "/travel" },
];

const WhatsAppIcon = ({ size = 15 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="#25D366"
    viewBox="0 0 16 16"
    style={{ flexShrink: 0 }}
  >
    <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
  </svg>
);

/* ── reCAPTCHA v2 hook ── */
function useRecaptcha(siteKey) {
  const [ready, setReady] = useState(false);
  const widgetIdRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Inject script once
    const SCRIPT_ID = "recaptcha-script";
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src =
        "https://www.google.com/recaptcha/api.js?render=explicit&onload=__rcOnload";
      s.async = true;
      s.defer = true;
      window.__rcOnload = () => setReady(true);
      document.head.appendChild(s);
    } else if (window.grecaptcha && window.grecaptcha.render) {
      setReady(true);
    } else {
      window.__rcOnload = () => setReady(true);
    }
  }, []);

  useEffect(() => {
    if (ready && containerRef.current && widgetIdRef.current === null) {
      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: siteKey,
        theme: "light",
        size: "normal",
      });
    }
  }, [ready, siteKey]);

  const getToken = () => {
    if (widgetIdRef.current === null) return null;
    return window.grecaptcha.getResponse(widgetIdRef.current);
  };

  const reset = () => {
    if (widgetIdRef.current !== null)
      window.grecaptcha.reset(widgetIdRef.current);
  };

  return { containerRef, getToken, reset };
}

/* ── Subscribe Modal ── */
// Replace YOUR_RECAPTCHA_SITE_KEY below with your actual reCAPTCHA v2 site key
const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || "YOUR_RECAPTCHA_SITE_KEY";

function SubscribeModal({ onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null | "success" | "duplicate" | "error" | "captcha"
  const [loading, setLoading] = useState(false);
  const { containerRef, getToken, reset } = useRecaptcha(RECAPTCHA_SITE_KEY);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    const captchaToken = getToken();
    if (!captchaToken) {
      setStatus("captcha");
      return;
    }

    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/subscribe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            email: email.trim(),
            captchaToken,
          }),
        }
      );
      if (res.status === 201) {
        setStatus("success");
      } else if (res.status === 409) {
        setStatus("duplicate");
        reset();
      } else {
        setStatus("error");
        reset();
      }
    } catch (err) {
      console.error("Subscribe error:", err);
      setStatus("error");
      reset();
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1.5px solid #e5e7eb",
    borderRadius: "7px",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Georgia', serif",
    transition: "border-color 0.15s",
  };

  const labelStyle = {
    display: "block",
    fontSize: "12px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "5px",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        backdropFilter: "blur(2px)",
        animation: "fadeInOverlay 0.2s ease",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "36px 28px 28px",
          width: "100%",
          maxWidth: "420px",
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          position: "relative",
          fontFamily: "'Georgia', serif",
          animation: "slideUpModal 0.28s cubic-bezier(.34,1.56,.64,1)",
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "14px",
            right: "16px",
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            color: "#9ca3af",
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {status === "success" ? (
          /* ── Success ── */
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🎉</div>
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: "20px",
                color: "#111",
                fontWeight: "700",
              }}
            >
              You're subscribed!
            </h3>
            <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>
              Welcome to BharatAbroad. We'll keep you updated with the latest
              stories.
            </p>
            <button
              onClick={onClose}
              style={{
                marginTop: "20px",
                padding: "10px 28px",
                background: "#f97316",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Done
            </button>
          </div>
        ) : (
          /* ── Form ── */
          <>
            <div style={{ marginBottom: "24px" }}>
              <p
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "1.5px",
                  color: "#f97316",
                  textTransform: "uppercase",
                  margin: "0 0 6px",
                }}
              >
                Newsletter
              </p>
              <h2
                style={{
                  margin: "0 0 6px",
                  fontSize: "22px",
                  color: "#111",
                  fontWeight: "700",
                  lineHeight: 1.3,
                }}
              >
                Stay Connected with BharatAbroad
              </h2>
              <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
                Get the latest news, stories &amp; updates delivered to your
                inbox.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Name */}
              <div style={{ marginBottom: "14px" }}>
                <label style={labelStyle}>Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Arjun Sharma"
                  required
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
              </div>

              {/* Email */}
              <div style={{ marginBottom: "18px" }}>
                <label style={labelStyle}>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="arjun@email.com"
                  required
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
              </div>

              {/* reCAPTCHA widget */}
              <div style={{ marginBottom: "16px" }}>
                <div ref={containerRef} />
                {status === "captcha" && (
                  <p
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "6px",
                      marginBottom: 0,
                    }}
                  >
                    Please complete the CAPTCHA verification.
                  </p>
                )}
              </div>

              {/* Error messages */}
              {status === "duplicate" && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "13px",
                    margin: "-4px 0 14px",
                  }}
                >
                  This email is already subscribed.
                </p>
              )}
              {status === "error" && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "13px",
                    margin: "-4px 0 14px",
                  }}
                >
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: loading ? "#fdba74" : "#f97316",
                  color: "#fff",
                  border: "none",
                  borderRadius: "7px",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.currentTarget.style.background = "#ea6c0a";
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.currentTarget.style.background = "#f97316";
                }}
              >
                {loading ? "Subscribing…" : "Subscribe — It's Free"}
              </button>

              <p
                style={{
                  marginTop: "12px",
                  fontSize: "11px",
                  color: "#9ca3af",
                  textAlign: "center",
                }}
              >
                No spam, ever. Unsubscribe anytime.
              </p>
            </form>
          </>
        )}
      </div>

      <style>{`
        @keyframes fadeInOverlay { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUpModal { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }
      `}</style>
    </div>
  );
}

/* ── Subscribe Button (reusable) ── */
function SubscribeBtn({ onOpen, compact = false }) {
  return (
    <button
      onClick={onOpen}
      style={{
        background: "#f97316",
        color: "#fff",
        border: "none",
        borderRadius: compact ? 5 : 6,
        padding: compact ? "5px 12px" : "6px 16px",
        fontSize: compact ? 11 : 12,
        fontWeight: 600,
        cursor: "pointer",
        letterSpacing: "0.3px",
        whiteSpace: "nowrap",
        transition: "background 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#ea6c0a";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#f97316";
      }}
    >
      Subscribe
    </button>
  );
}

/* ── Main Navbar ── */
function AppNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {showModal && <SubscribeModal onClose={() => setShowModal(false)} />}

      {/* ========== MOBILE & TABLET (< lg) ========== */}
      <div className="d-lg-none">
        <Navbar
          bg="light"
          expand="lg"
          fixed="top"
          style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.08)", padding: "6px 0" }}
        >
          <Container>
            <Navbar.Brand as={Link} to="/">
              <img
                src={logo}
                alt="Logo"
                style={{ height: "40px" }}
                className="img-fluid"
              />
            </Navbar.Brand>

            {/* Right cluster: WhatsApp icon + hamburger only (subscribe lives in drawer) */}
            <div className="d-flex align-items-center gap-2 ms-auto me-2">
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                title="Join WhatsApp Community"
                style={{
                  color: "#25D366",
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: "#f0fdf4",
                  textDecoration: "none",
                  gap: 5,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <WhatsAppIcon size={18} />
                <span
                  className="d-none d-sm-inline"
                  style={{ whiteSpace: "nowrap" }}
                >
                  Community
                </span>
              </a>
            </div>

            <Navbar.Toggle aria-controls="mobile-navbar-nav" />

            <Navbar.Collapse id="mobile-navbar-nav">
              <Nav className="mt-2 mt-lg-0 w-100">
                {/* Divider */}
                <div
                  style={{
                    height: 1,
                    background: "#f0f0f0",
                    margin: "4px 0 6px",
                  }}
                />
                {NAV_LINKS.map(({ label, to }) => (
                  <Nav.Link
                    key={to}
                    as={Link}
                    to={to}
                    style={{
                      color: "#111",
                      fontWeight: 500,
                      fontSize: 15,
                      padding: "10px 16px",
                      borderRadius: 8,
                      margin: "1px 4px",
                    }}
                  >
                    {label}
                  </Nav.Link>
                ))}
                {/* Subscribe CTA at bottom of drawer */}
                <div style={{ padding: "12px 8px 8px" }}>
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #c2410c 0%, #f97316 100%)",
                      borderRadius: 12,
                      padding: "16px",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 4px",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 15,
                      }}
                    >
                      ✉ Stay informed
                    </p>
                    <p
                      style={{
                        margin: "0 0 12px",
                        color: "rgba(255,255,255,0.7)",
                        fontSize: 12,
                        lineHeight: 1.4,
                      }}
                    >
                      Get daily news, breaking alerts &amp; more
                    </p>
                    <button
                      onClick={() => setShowModal(true)}
                      style={{
                        width: "100%",
                        background: "#fff",
                        color: "#f97316",
                        border: "none",
                        borderRadius: 8,
                        padding: "11px",
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: "pointer",
                        letterSpacing: "0.2px",
                      }}
                    >
                      Subscribe — Free &amp; Paid Plans
                    </button>
                  </div>
                </div>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>

        <div style={{ marginTop: "64px" }} />
        <NewsBanner />
      </div>

      {/* ========== DESKTOP (>= lg) ========== */}
      <div className="d-none d-lg-block">
        {/* Line 1: Top info bar */}
        <div
          style={{
            background: "#f8f9fa",
            borderBottom: "1px solid #e0e0e0",
            padding: "8px 0",
            fontSize: "13px",
          }}
        >
          <Container fluid className="px-4">
            <div className="d-flex justify-content-between align-items-center">
              {/* Left: date */}
              <div style={{ color: "#666" }}>
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>

              {/* Right: WhatsApp + Subscribe */}
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: "#25D366",
                    fontWeight: 600,
                    fontSize: 13,
                    textDecoration: "none",
                  }}
                >
                  <WhatsAppIcon size={15} />
                  Join our WhatsApp Community
                </a>

                {/* Divider */}
                <div style={{ width: 1, height: 16, background: "#ddd" }} />

                <SubscribeBtn onOpen={() => setShowModal(true)} />
              </div>
            </div>
          </Container>
        </div>

        {/* Line 2: Centered logo */}
        <div
          style={{
            padding: "22px 0",
            background: "#fff",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Container>
            <div className="text-center">
              <Link to="/">
                <img
                  src={logo}
                  alt="Logo"
                  style={{ height: "80px" }}
                  className="img-fluid"
                />
              </Link>
            </div>
          </Container>
        </div>

        {/* Line 3: Main nav */}
        <Navbar
          bg="white"
          style={{
            borderTop: "0.2px solid #bed7fdff",
            borderBottom: "0.2px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <Container>
            <Nav className="mx-auto">
              {NAV_LINKS.map(({ label, to }) => (
                <Nav.Link
                  key={to}
                  as={Link}
                  to={to}
                  className="px-3 fw-semibold"
                  style={{ color: "#222" }}
                >
                  {label}
                </Nav.Link>
              ))}
            </Nav>
          </Container>
        </Navbar>

        {/* Line 4: News ticker */}
        <NewsBanner />

        {/* Sticky navbar on scroll */}
        {scrolled && (
          <Navbar
            bg="white"
            fixed="top"
            style={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderBottom: "0.2px solid #0066ff",
            }}
          >
            <Container>
              <Navbar.Brand as={Link} to="/">
                <img
                  src={logo}
                  alt="Logo"
                  style={{ height: "35px" }}
                  className="img-fluid"
                />
              </Navbar.Brand>
              <Nav className="mx-auto">
                {NAV_LINKS.map(({ label, to }) => (
                  <Nav.Link
                    key={to}
                    as={Link}
                    to={to}
                    className="px-3"
                    style={{ color: "#222" }}
                  >
                    {label}
                  </Nav.Link>
                ))}
              </Nav>
              {/* Subscribe in sticky too */}
              <div className="ms-3">
                <SubscribeBtn onOpen={() => setShowModal(true)} compact />
              </div>
            </Container>
          </Navbar>
        )}
      </div>
    </>
  );
}

export default AppNavbar;
